<script setup lang="ts">
import { type ChatMsg, useHelpChat } from '@/composables/useHelpChat';
import { renderUntrustedMarkdown } from '@/composables/useMarkdown';

const emit = defineEmits<{ close: [] }>();

const {
  messages,
  isLoading,
  isUnavailable,
  rateLimitSecondsLeft,
  canSend,
  sendMessage,
  stopStream,
  clearSession,
} = useHelpChat();

const titleId = `help-ai-chat-title-${useId()}`;
const inputText = ref('');
const messagesEl = ref<HTMLElement>();
const textareaEl = ref<HTMLTextAreaElement>();

const renderedCache = new Map<string, { text: string; html: string }>();

function renderedHtml(msg: ChatMsg): string {
  if (msg.isStreaming) {
    return renderUntrustedMarkdown(msg.text);
  }
  const cached = renderedCache.get(msg.id);
  if (cached?.text === msg.text) {
    return cached.html;
  }
  const html = renderUntrustedMarkdown(msg.text);
  renderedCache.set(msg.id, { text: msg.text, html });

  return html;
}

watch(
  () => messages.value.length,
  () => {
    const ids = new Set(messages.value.map((m) => m.id));
    for (const id of renderedCache.keys()) {
      if (!ids.has(id)) {
        renderedCache.delete(id);
      }
    }
  },
);

function scrollToBottom() {
  nextTick(() => {
    if (messagesEl.value) {
      messagesEl.value.scrollTop = messagesEl.value.scrollHeight;
    }
  });
}

function focusInput() {
  nextTick(() => textareaEl.value?.focus());
}

watch(
  () => [messages.value.length, messages.value.at(-1)?.text],
  scrollToBottom,
);

watch(isLoading, (loading) => {
  if (!loading) {
    focusInput();
  }
});

onMounted(() => {
  scrollToBottom();
  focusInput();
});

const sendDisabled = computed(() => !canSend.value || !inputText.value.trim());

function send() {
  if (sendDisabled.value) {
    return;
  }
  const text = inputText.value;
  inputText.value = '';
  void sendMessage(text);
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
    event.preventDefault();
    send();
  }
}

function handleClear() {
  clearSession();
  focusInput();
}

function handleClose() {
  stopStream();
  emit('close');
}
</script>

<template>
  <div
    class="help-ai-chat"
    role="dialog"
    :aria-labelledby="titleId"
    @keydown.esc.stop="handleClose"
  >
    <div class="ai-chat-header">
      <div class="ai-header-icon">
        <UiIcon name="Sparkles" :size="20" />
      </div>
      <span :id="titleId" class="ai-header-title">
        {{ $t('help.ai.headerTitle') }}
      </span>
      <UiButton
        v-if="messages.length > 0"
        variant="flat"
        icon="Trash2"
        class="ai-header-btn"
        :title="$t('help.ai.clearChat')"
        :aria-label="$t('help.ai.clearChat')"
        @click="handleClear"
      />
      <UiButton
        variant="flat"
        icon="XIcon"
        class="ai-header-btn"
        :title="$t('help.ai.close')"
        :aria-label="$t('help.ai.close')"
        @click="handleClose"
      />
    </div>

    <div
      ref="messagesEl"
      class="ai-messages"
      role="log"
      aria-live="polite"
      :aria-label="$t('help.ai.conversation')"
    >
      <div v-if="messages.length === 0" class="ai-msg-row">
        <div class="ai-avatar">
          <UiIcon name="Sparkles" :size="14" />
        </div>
        <div class="ai-msg ai-msg--assistant">
          {{ $t('help.ai.welcome') }}
        </div>
      </div>

      <template v-for="msg in messages" :key="msg.id">
        <div v-if="msg.role === 'assistant'" class="ai-msg-row">
          <div class="ai-avatar">
            <UiIcon name="Sparkles" :size="14" />
          </div>
          <div
            :class="[
              'ai-msg',
              'ai-msg--assistant',
              { 'ai-msg--error': msg.isError },
            ]"
            :aria-busy="msg.isStreaming ? 'true' : undefined"
          >
            <span v-if="msg.isStreaming && !msg.text" class="ai-thinking">
              <template v-if="msg.statusText === 'inspecting'">
                <UiIcon name="Search" :size="12" />
                {{ $t('help.ai.inspecting') }}
              </template>
              <template v-else-if="msg.statusText === 'queued'">
                {{ $t('help.ai.queued') }}
              </template>
              <template v-else>
                <span class="ai-dot" /><span class="ai-dot" /><span
                  class="ai-dot"
                />
              </template>
            </span>
            <span v-else-if="msg.isError">{{ msg.text }}</span>
            <template v-else>
              <!-- eslint-disable vue/no-v-html -->
              <div
                v-if="msg.text"
                class="markdown-content"
                v-html="renderedHtml(msg)"
              />
              <!-- eslint-enable vue/no-v-html -->
              <span v-if="msg.isStopped" class="ai-msg-note">
                {{ $t('help.ai.stopped') }}
              </span>
            </template>
          </div>
        </div>

        <div v-else class="ai-msg-row ai-msg-row--user">
          <div class="ai-msg ai-msg--user">{{ msg.text }}</div>
        </div>
      </template>
    </div>

    <div class="ai-input-row">
      <textarea
        ref="textareaEl"
        v-model="inputText"
        :placeholder="$t('help.ai.placeholder')"
        :aria-label="$t('help.ai.placeholder')"
        :disabled="isLoading || isUnavailable"
        rows="3"
        class="ai-textarea"
        @keydown="handleKeydown"
      />
      <UiButton
        v-if="isLoading"
        icon="Octagon"
        class="ai-round-btn"
        :title="$t('help.ai.stop')"
        :aria-label="$t('help.ai.stop')"
        @click="stopStream"
      />
      <UiButton
        v-else
        variant="primary"
        icon="ArrowUp"
        class="ai-round-btn"
        :disabled="sendDisabled"
        :title="
          rateLimitSecondsLeft > 0
            ? $t('help.ai.rateLimitedShort', { seconds: rateLimitSecondsLeft })
            : $t('help.ai.send')
        "
        :aria-label="$t('help.ai.send')"
        @click="send"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.help-ai-chat {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ai-chat-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--space-8);
  padding: px(14) var(--space-16);
  background-color: var(--color-primary-900);
  color: var(--color-gray-0);
  --color-focus-ring: var(--color-gray-0);

  .ai-header-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    margin-right: var(--space-4);
    border-radius: var(--border-radius-12);
    background-color: var(--color-primary-700);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ai-header-title {
    flex: 1;
    font-size: var(--font-size-16);
    font-weight: var(--font-weight-bold);
    line-height: var(--line-height-ratio-120);
  }

  .ai-header-btn.ui-button {
    flex-shrink: 0;
    color: var(--color-gray-0);

    &:hover {
      background-color: var(--color-primary-700);
    }
  }
}

.ai-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-16) px(14);
  display: flex;
  flex-direction: column;
  gap: px(14);
  background-color: var(--color-gray-0);

  .ai-msg-row {
    display: flex;
    align-items: flex-end;
    gap: var(--space-8);

    &--user {
      flex-direction: row-reverse;
    }
  }

  .ai-avatar {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: var(--border-radius-circle);
    background-color: var(--color-primary-900);
    color: var(--color-gray-0);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ai-msg {
    max-width: 78%;
    padding: var(--space-10) px(14);
    border-radius: px(18);
    font-size: var(--font-size-14);
    line-height: 1.55;
    word-break: break-word;

    &--user {
      white-space: pre-wrap;
      background-color: var(--color-primary-900);
      color: var(--color-gray-0);
      border-bottom-right-radius: var(--border-radius-4);
    }

    &--assistant {
      background-color: var(--color-primary-0);
      color: var(--color-gray-900);
      border-bottom-left-radius: var(--border-radius-4);
    }

    &--error {
      background-color: var(--color-error-50);
      color: var(--color-error-700);
    }
  }

  .ai-msg-note {
    display: block;
    font-size: var(--font-size-12);
    font-style: italic;
    color: var(--color-gray-500);
  }

  .ai-thinking {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-2) 0;
  }

  .ai-dot {
    width: 7px;
    height: 7px;
    border-radius: var(--border-radius-circle);
    background-color: var(--color-gray-400);
    animation: bounce-dot 1.2s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes bounce-dot {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  40% {
    transform: translateY(-5px);
    opacity: 1;
  }
}

.ai-input-row {
  flex-shrink: 0;
  margin: 0 var(--space-12) var(--space-10);
  border: 1.5px solid var(--color-primary-900);
  border-radius: var(--border-radius-14);
  background-color: var(--color-gray-0);
  display: flex;
  align-items: flex-end;
  gap: var(--space-6);
  padding: var(--space-6) var(--space-8) var(--space-8);
  // The textarea keeps its reset; the ring is drawn over the row's navy border
  // (outside it, the two made a double frame), and not for the send and stop
  // buttons, which have their own.
  @include focus-ring-within($inset: true, $field: '.ai-textarea');

  .ai-textarea {
    flex: 1;
    resize: none;
    border: none;
    outline: none;
    padding: var(--space-6) var(--space-6) var(--space-4);
    font-size: var(--font-size-14);
    font-family: inherit;
    color: var(--color-gray-900);
    background: transparent;
    line-height: var(--line-height-ratio-140);
    min-height: px(44);
    max-height: px(110);
    overflow-y: auto;

    &::placeholder {
      color: var(--color-gray-400);
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .ai-round-btn.ui-button {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    padding: 0;
    border-radius: var(--border-radius-circle);
  }
}

.ai-msg--assistant {
  :deep(.markdown-content) {
    > p:first-child {
      margin-top: 0;
    }
    > p:last-child {
      margin-bottom: 0;
    }

    p {
      margin: var(--space-4) 0;
      line-height: 1.55;
    }
    ul,
    ol {
      margin: var(--space-4) 0 var(--space-4) var(--space-16);
      padding: 0;
    }
    li {
      margin: var(--space-2) 0;
    }
    strong {
      font-weight: 600;
    }
    em {
      font-style: italic;
    }
    a {
      color: var(--color-primary-600);
      text-decoration: underline;
    }

    h1,
    h2,
    h3 {
      font-weight: 600;
      margin: var(--space-8) 0 var(--space-4);
      line-height: var(--line-height-ratio-130);
    }
    h1 {
      font-size: var(--font-size-15);
    }
    h2 {
      font-size: var(--font-size-14);
    }
    h3 {
      font-size: px(13.5);
    }

    code {
      font-family: monospace;
      font-size: var(--font-size-12);
      background-color: var(--color-gray-100);
      border-radius: px(3);
      padding: 0 var(--space-4);
    }
    pre {
      white-space: pre;
      background-color: var(--color-gray-100);
      border-radius: var(--border-radius-6);
      padding: var(--space-8) var(--space-10);
      overflow-x: auto;
      margin: var(--space-6) 0;
      code {
        background: none;
        padding: 0;
      }
    }
  }
}
</style>
